import { Button, Typography } from "@mui/material";
import Toolbar from "@mui/material/Toolbar";
import { Cart20Px, ManualPick20Px, Requirement20Px } from "@qubit/autoparts";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { NavBarButtonGroup, UnselectButton } from "~/features/navbar/Navbar";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { canBatchBePicked } from "~/lib/fcConfigHelpers";
import { canPrepBatchOption } from "~/lib/helpers";

import { mixpanelTrack, useMixpanelContextName } from "~/lib/mixpanel-tracking";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";

import { resetSelectedBatch } from "./pickBatches.slice";

export function PickBatchesToolbar() {
  const navigate = useFcNavigate();
  const dispatch = useAppDispatch();

  const { t } = useTranslation();
  const trackedPageName = useMixpanelContextName("Toolbar");

  const {
    cartPrepEnabled,
    bat_pickFromBatchesEnabled,
    bat_stageFromBatchesEnabled
  } = useAppSelector(selectServerConfig);

  const fulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);

  const selectedBatch = useAppSelector(
    (state) => state.pickBatches.selectedBatch
  );

  const canPickBatch =
    fulfillmentCenter &&
    selectedBatch &&
    canBatchBePicked(selectedBatch) &&
    ["scheduled", "cart ready", "processing"].includes(
      (selectedBatch && selectedBatch.status.toLowerCase()) || ""
    );

  const canStageBatch = ["picked", "dropped"].includes(
    (selectedBatch && selectedBatch.status.toLowerCase()) || ""
  );

  const canPrepBatch = canPrepBatchOption(selectedBatch);

  const handleUnselectBatch = useCallback(() => {
    dispatch(resetSelectedBatch());
  }, [dispatch]);

  return (
    <Toolbar>
      <UnselectButton onClick={handleUnselectBatch} />
      <NavBarButtonGroup>
        {selectedBatch &&
          selectedBatch.batchType !== "Bulk" &&
          cartPrepEnabled &&
          canPrepBatch && (
            <Button
              color="secondary"
              onClick={(): void => {
                mixpanelTrack({
                  trackedPageName,
                  type: "Button Click",
                  label: "Prep"
                });
                navigate(`/batches/${selectedBatch.batchName}/cart-prep`);
              }}
              startIcon={<Cart20Px fill="#fff" />}
            >
              <Typography variant="body2" style={{ color: "#fff" }}>
                {t("prep")}
              </Typography>
            </Button>
          )}
        {bat_pickFromBatchesEnabled &&
          selectedBatch &&
          selectedBatch.batchType !== "Autostore" &&
          canPickBatch && (
            <Button
              color="secondary"
              onClick={(): void => {
                mixpanelTrack({
                  trackedPageName,
                  type: "Button Click",
                  label: "Pick"
                });
                navigate(`/batches/${selectedBatch.batchName}/pick`);
              }}
              startIcon={<ManualPick20Px fill="#fff" />}
            >
              <Typography variant="body2" style={{ color: "#fff" }}>
                {t("pick verb")}
              </Typography>
            </Button>
          )}
        {bat_stageFromBatchesEnabled && selectedBatch && canStageBatch && (
          <Button
            color="secondary"
            onClick={(): void => {
              mixpanelTrack({
                trackedPageName,
                type: "Button Click",
                label: "Stage"
              });
              navigate(`/batches/${selectedBatch.batchName}/stage`);
            }}
            startIcon={<Requirement20Px fill="#fff" />}
          >
            <Typography variant="body2" style={{ color: "#fff" }}>
              {t("stage_verb")}
            </Typography>
          </Button>
        )}
      </NavBarButtonGroup>
    </Toolbar>
  );
}
